import { LETTERS, NOTES, COLORS, SHAPES, NUMBERS, FACES, ARROWS, MOVEMENTS, PATTERNS, BORDERS, VOICE_TYPES, ORBITAL_POSITIONS, CIRCLE_SIZES, CIRCLE_ROTATIONS, CIRCLE_BRIGHTNESS, ALL_CATEGORY_WORDS, getCategoryOfWord, CATEGORY_WORDS, NAMES, getRandomEquationForAnswer, RHYTHMS, MANDARIN_TONES, ALL_MANDARIN_WORDS, getToneOfWord, MANDARIN_TONE_WORDS, VOLUMES, VOLUMES_SPOKEN, VOLUMES_MUSICAL } from '../src/constants/gameConstants';

// Generate a random value for a dimension
export const generateRandomValue = (dimension, activeDimensions = []) => {
  switch(dimension) {
    case 'position': return Math.floor(Math.random() * 9);
    case 'audio': return LETTERS[Math.floor(Math.random() * LETTERS.length)];
    case 'voice': return VOICE_TYPES[Math.floor(Math.random() * VOICE_TYPES.length)];
    case 'note': return NOTES[Math.floor(Math.random() * NOTES.length)];
    case 'color': return COLORS[Math.floor(Math.random() * COLORS.length)];
    case 'shape': return SHAPES[Math.floor(Math.random() * SHAPES.length)];
    case 'number': return NUMBERS[Math.floor(Math.random() * NUMBERS.length)];
    case 'face': return FACES[Math.floor(Math.random() * FACES.length)];
    case 'arrow': return ARROWS[Math.floor(Math.random() * ARROWS.length)];
    case 'movement': return MOVEMENTS[Math.floor(Math.random() * MOVEMENTS.length)];
    case 'pattern': return PATTERNS[Math.floor(Math.random() * PATTERNS.length)];
    case 'border': return BORDERS[Math.floor(Math.random() * BORDERS.length)];
    case 'orbitalPosition': return ORBITAL_POSITIONS[Math.floor(Math.random() * ORBITAL_POSITIONS.length)];
    case 'circleSize': return CIRCLE_SIZES[Math.floor(Math.random() * CIRCLE_SIZES.length)];
    case 'circleRotation': return CIRCLE_ROTATIONS[Math.floor(Math.random() * CIRCLE_ROTATIONS.length)];
    case 'circleBrightness': return CIRCLE_BRIGHTNESS[Math.floor(Math.random() * CIRCLE_BRIGHTNESS.length)];
    case 'categoryText': return ALL_CATEGORY_WORDS[Math.floor(Math.random() * ALL_CATEGORY_WORDS.length)];
    case 'categoryAudio': return ALL_CATEGORY_WORDS[Math.floor(Math.random() * ALL_CATEGORY_WORDS.length)];
    case 'nameText': return NAMES[Math.floor(Math.random() * NAMES.length)];
    case 'nameAudio': return NAMES[Math.floor(Math.random() * NAMES.length)];
    case 'math': return Math.floor(Math.random() * 10); // Returns answer (0-9)
    case 'rhythm': return RHYTHMS[Math.floor(Math.random() * RHYTHMS.length)].id;
    case 'mandarinTone': return ALL_MANDARIN_WORDS[Math.floor(Math.random() * ALL_MANDARIN_WORDS.length)];
    case 'volume': {
      // Choose volume scale based on active dimensions
      // Use musical scale if note or rhythm is active, spoken scale otherwise
      const isMusical = activeDimensions.includes('note') || activeDimensions.includes('rhythm');
      const volumeArray = isMusical ? VOLUMES_MUSICAL : VOLUMES_SPOKEN;
      return volumeArray[Math.floor(Math.random() * volumeArray.length)];
    }
    default: return null;
  }
};

// Generate a different value from the given one
export const generateDifferentValue = (dimension, currentValue, activeDimensions = []) => {
  let newValue;
  let attempts = 0;
  const maxAttempts = 20;
  
  // For category dimensions, ensure different category not just different word
  if (dimension === 'categoryText' || dimension === 'categoryAudio') {
    const currentCategory = getCategoryOfWord(currentValue);
    do {
      newValue = generateRandomValue(dimension, activeDimensions);
      const newCategory = getCategoryOfWord(newValue);
      attempts++;
      // Different if it's a different category
      if (newCategory !== currentCategory) break;
    } while (attempts < maxAttempts);
  } 
  // For Mandarin tone dimension, ensure different tone not just different syllable
  else if (dimension === 'mandarinTone') {
    const currentTone = getToneOfWord(currentValue);
    do {
      newValue = generateRandomValue(dimension, activeDimensions);
      const newTone = getToneOfWord(newValue);
      attempts++;
      // Different if it's a different tone
      if (newTone !== currentTone) break;
    } while (attempts < maxAttempts);
  } 
  else {
    do {
      newValue = generateRandomValue(dimension, activeDimensions);
      attempts++;
    } while (newValue === currentValue && attempts < maxAttempts);
  }
  
  return newValue;
};

// Generate match schedule - exactly 6 matches per dimension in 20 trials
export const generateMatchSchedule = (activeDimensions, opportunityTrials = 20) => {
  const schedule = {};
  activeDimensions.forEach(dimension => {
    // Create array of 20 positions, 6 will be matches
    const positions = Array(opportunityTrials).fill(false);
    const matchPositions = [];
    
    // Randomly select 6 positions for matches
    while (matchPositions.length < 6) {
      const pos = Math.floor(Math.random() * opportunityTrials);
      if (!matchPositions.includes(pos)) {
        matchPositions.push(pos);
      }
    }
    
    matchPositions.forEach(pos => {
      positions[pos] = true;
    });
    
    schedule[dimension] = positions;
  });
  return schedule;
};

// Generate stimulus for a trial
export const generateStimulus = (
  trialIndex,
  schedule,
  historyArray,
  activeDimensions,
  nValue,
  interferenceRate
) => {
  const hasAudio = activeDimensions.includes('audio');
  const hasVoice = activeDimensions.includes('voice');
  
  // Check if we're in the opportunity window and should create matches
  const isOpportunityTrial = trialIndex !== null && trialIndex >= nValue;
  const opportunityIndex = isOpportunityTrial ? trialIndex - nValue : null;
  
  let stimulus = {};
  
  // For each dimension, determine if it should match or be random
  const dimensions = ['position', 'audio', 'voice', 'note', 'color', 'shape', 'number', 'face', 'arrow', 'movement', 'pattern', 'border', 'orbitalPosition', 'circleSize', 'circleRotation', 'circleBrightness', 'categoryText', 'categoryAudio', 'nameText', 'nameAudio', 'math', 'rhythm', 'mandarinTone', 'volume'];
  
  dimensions.forEach(dimension => {
    // Check if this dimension is active
    const isDimensionActive = activeDimensions.includes(dimension);
    
    if (!isDimensionActive) {
      // For inactive dimensions, just generate random
      stimulus[dimension] = generateRandomValue(dimension, activeDimensions);
      return;
    }
    
    // Check if this dimension should match (from schedule)
    const shouldMatch = isOpportunityTrial && 
                       schedule && 
                       schedule[dimension] && 
                       schedule[dimension][opportunityIndex] &&
                       historyArray &&
                       historyArray.length >= nValue;
    
    if (shouldMatch) {
      // Copy from n-back (scheduled match)
      const nBackStimulus = historyArray[historyArray.length - nValue];
      
      // For category dimensions, pick a DIFFERENT word from the SAME category
      if (dimension === 'categoryText' || dimension === 'categoryAudio') {
        const nBackWord = nBackStimulus[dimension];
        const category = getCategoryOfWord(nBackWord);
        if (category) {
          const categoryWords = CATEGORY_WORDS[category];
          // Pick a random word from the same category (might be same, might be different)
          stimulus[dimension] = categoryWords[Math.floor(Math.random() * categoryWords.length)];
        } else {
          stimulus[dimension] = nBackWord; // Fallback to exact match
        }
      }
      // For Mandarin tones, pick a DIFFERENT syllable from the SAME tone
      else if (dimension === 'mandarinTone') {
        const nBackWord = nBackStimulus[dimension];
        const tone = getToneOfWord(nBackWord);
        if (tone) {
          const toneWords = MANDARIN_TONE_WORDS[tone];
          // Pick a random word from the same tone (might be same, might be different)
          stimulus[dimension] = toneWords[Math.floor(Math.random() * toneWords.length)];
        } else {
          stimulus[dimension] = nBackWord; // Fallback to exact match
        }
      }
      // For math, pick a DIFFERENT equation with the SAME answer
      else if (dimension === 'math') {
        const nBackAnswer = nBackStimulus[dimension];
        // Store the same answer (equation will be randomly generated when displaying)
        stimulus[dimension] = nBackAnswer;
      }
      // For all other dimensions, exact match
      else {
        stimulus[dimension] = nBackStimulus[dimension];
      }
    } else if (isOpportunityTrial && historyArray && historyArray.length >= nValue) {
      // Non-match trial in opportunity window - maybe add interference
      const useInterference = Math.random() < interferenceRate;
      
      if (useInterference) {
        // Create a trick trial
        const trickType = Math.random();
        let trickStimulus = null;
        
        if (trickType < 0.4 && historyArray.length >= nValue - 1) {
          // Trick type 1: Match N-1 trials ago (40% of tricks)
          trickStimulus = historyArray[historyArray.length - (nValue - 1)];
        } else if (trickType < 0.7 && historyArray.length >= nValue + 1) {
          // Trick type 2: Match N+1 trials ago (30% of tricks)
          trickStimulus = historyArray[historyArray.length - (nValue + 1)];
        } else if (trickType < 0.9 && historyArray.length >= nValue * 2) {
          // Trick type 3: Match 2N trials ago (20% of tricks)
          trickStimulus = historyArray[historyArray.length - (nValue * 2)];
        } else if (historyArray.length >= nValue && activeDimensions.length >= 2) {
          // Trick type 4: Swap dimensions from N trials ago (10% of tricks)
          const nBackStimulus = historyArray[historyArray.length - nValue];
          const activeValues = activeDimensions.map(dim => nBackStimulus[dim]);
          
          // Shuffle the values
          const shuffledValues = [...activeValues];
          for (let i = shuffledValues.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffledValues[i], shuffledValues[j]] = [shuffledValues[j], shuffledValues[i]];
          }
          
          // Assign shuffled values to dimensions
          activeDimensions.forEach((dim, idx) => {
            if (dim === dimension) {
              stimulus[dimension] = shuffledValues[idx];
            }
          });
          return; // Skip the rest for this dimension
        }
        
        if (trickStimulus) {
          stimulus[dimension] = trickStimulus[dimension];
        } else {
          // Fallback to different value
          const nBackStimulus = historyArray[historyArray.length - nValue];
          stimulus[dimension] = generateDifferentValue(dimension, nBackStimulus[dimension], activeDimensions);
        }
      } else {
        // No interference - generate different from N-back
        const nBackStimulus = historyArray[historyArray.length - nValue];
        stimulus[dimension] = generateDifferentValue(dimension, nBackStimulus[dimension], activeDimensions);
      }
    } else {
      // Setup phase or not enough history - just random
      stimulus[dimension] = generateRandomValue(dimension, activeDimensions);
    }
  });
  
  // Handle audio/voice special logic for when only one is active
  if (hasAudio && !hasVoice) {
    stimulus.voice = 'male1'; // Fixed voice
  } else if (!hasAudio && hasVoice) {
    stimulus.audio = 'C'; // Fixed letter
  }
  
  // For math dimension, generate a random equation display for the answer
  if (stimulus.math !== undefined && stimulus.math !== null) {
    stimulus.mathDisplay = getRandomEquationForAnswer(stimulus.math);
  }
  
  return stimulus;
};

// Check if dimensions match
export const checkMatch = (dimension, currentStimulus, history, nValue) => {
  if (history.length < nValue) return false;
  const nBack = history[history.length - nValue];
  
  // For category dimensions, match by category not exact word
  if (dimension === 'categoryText' || dimension === 'categoryAudio') {
    const currentCategory = getCategoryOfWord(currentStimulus[dimension]);
    const nBackCategory = getCategoryOfWord(nBack[dimension]);
    return currentCategory === nBackCategory && currentCategory !== null;
  }
  
  // For Mandarin tone dimension, match by tone not exact syllable
  if (dimension === 'mandarinTone') {
    const currentTone = getToneOfWord(currentStimulus[dimension]);
    const nBackTone = getToneOfWord(nBack[dimension]);
    return currentTone === nBackTone && currentTone !== null;
  }
  
  return currentStimulus[dimension] === nBack[dimension];
};

// Calculate level after session
export const calculateLevelRecommendation = (accuracy, activeDimensions, currentNValue) => {
  let shouldLevelUp = true;
  let shouldLevelDown = false;
  const dimensionResults = {};

  activeDimensions.forEach(dimension => {
    const dimAccuracy = accuracy[dimension] || [];
    const correctCount = dimAccuracy.filter(Boolean).length;
    const total = dimAccuracy.length; // Should be 20 (opportunity trials)
    const percentage = total > 0 ? (correctCount / total) * 100 : 0;
    
    dimensionResults[dimension] = {
      correct: correctCount,
      total: total,
      percentage: Math.round(percentage)
    };

    if (percentage < 90) shouldLevelUp = false;
    if (percentage < 75) shouldLevelDown = true;
  });

  let newLevel = currentNValue;
  let recommendation = '';
  
  if (shouldLevelUp && currentNValue < 10) {
    newLevel = currentNValue + 1;
    recommendation = 'level_up';
  } else if (shouldLevelDown && currentNValue > 1) {
    newLevel = currentNValue - 1;
    recommendation = 'level_down';
  } else {
    recommendation = 'same_level';
  }

  return {
    dimensionResults,
    newLevel,
    recommendation
  };
};

// Get dimension combo key (sorted for consistency)
export const getDimensionComboKey = (dimensions) => {
  return [...dimensions].sort().join(',');
};

